import { useState } from 'react'
import { useRunShares, useCreateRunShare, useUpdateRunShare, useDeleteRunShare } from '../../hooks/useRunShares'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { Alert, AlertDescription } from '../ui/alert'
import { Copy, Share, Trash2 } from 'lucide-react'

interface SharingControlsProps {
  runId: string
}

export function SharingControls({ runId }: SharingControlsProps) {
  const { data: shares, isLoading } = useRunShares(runId)
  const createShareMutation = useCreateRunShare()
  const updateShareMutation = useUpdateRunShare()
  const deleteShareMutation = useDeleteRunShare()
  const [copySuccess, setCopySuccess] = useState(false)

  const currentShare = shares?.[0] // Assuming one share per run for MVP

  const handleCreateShare = async () => {
    await createShareMutation.mutateAsync(runId)
  }

  const handleTogglePublic = async (isPublic: boolean) => {
    if (currentShare) {
      await updateShareMutation.mutateAsync({
        id: currentShare.id,
        updates: { is_public: isPublic }
      })
    }
  }

  const handleDeleteShare = async () => {
    if (currentShare && window.confirm('Are you sure you want to stop sharing this run?')) {
      await deleteShareMutation.mutateAsync(currentShare.id)
    }
  }

  const handleCopyLink = async () => {
    if (currentShare) {
      const url = `${window.location.origin}/shared/${currentShare.slug}`
      try {
        await navigator.clipboard.writeText(url)
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = url
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      }
    }
  }

  if (isLoading) return (
    <div className="flex items-center justify-center py-8">
      <div className="text-neutral-600">Loading sharing settings...</div>
    </div>
  )

  return (
    <Card className="bg-white border-neutral-200 shadow-minimal">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold text-primary-900">Public Sharing</CardTitle>
        <p className="text-sm text-neutral-600">
          Share your run plan with others via a public link
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {!currentShare ? (
          <div className="text-center py-8">
            <div className="p-6 bg-neutral-50 rounded-xl border border-neutral-200 mb-6">
              <p className="text-neutral-600 mb-4 font-medium">
                This run is not currently shared publicly
              </p>
            </div>
            <Button
              onClick={handleCreateShare}
              disabled={createShareMutation.isPending}
              size="lg"
              className="bg-primary-600 hover:bg-primary-700"
            >
              <Share className="h-5 w-5 mr-2" />
              {createShareMutation.isPending ? 'Creating...' : 'Create Public Link'}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl border border-neutral-200">
              <div>
                <Label htmlFor="public-toggle" className="text-base font-semibold text-primary-900">Public access</Label>
                <p className="text-sm text-neutral-600 mt-1">Allow anyone with the link to view this run</p>
              </div>
              <Switch
                id="public-toggle"
                checked={currentShare.is_public ?? false}
                onCheckedChange={handleTogglePublic}
                disabled={updateShareMutation.isPending}
              />
            </div>

            {currentShare.is_public && (
              <div className="space-y-3">
                <Label className="text-base font-semibold text-primary-900">Share URL</Label>
                <div className="flex gap-3">
                  <Input
                    value={`${window.location.origin}/shared/${currentShare.slug}`}
                    readOnly
                    className="font-mono text-sm bg-neutral-50 border-neutral-300"
                  />
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    size="sm"
                    className="bg-white hover:bg-neutral-50 border-neutral-300 shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {copySuccess && (
                  <p className="text-sm text-emerald-700 font-medium bg-emerald-50 p-2 rounded-lg border border-emerald-200">
                    ✓ Link copied to clipboard!
                  </p>
                )}
              </div>
            )}

            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800">
                <strong>Privacy Note:</strong> Anyone with this link can view your run plan, but they cannot edit it.
                Personal information like your name is not included in the shared view.
              </AlertDescription>
            </Alert>

            <div className="flex justify-between items-center pt-6 border-t border-neutral-200">
              <div>
                <p className="text-base font-semibold text-primary-900">Manage sharing</p>
                <p className="text-sm text-neutral-600 mt-1">
                  {currentShare.created_at && `Created ${new Date(currentShare.created_at).toLocaleDateString()}`}
                </p>
              </div>
              <Button
                onClick={handleDeleteShare}
                variant="outline"
                size="sm"
                className="text-accent-700 hover:text-accent-800 hover:bg-accent-50 border-accent-200"
                disabled={deleteShareMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Stop Sharing
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}